import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import {
  HsButtonModule,
  HsFormFieldModule,
  HsInputModule,
  HsSearchBarModule,
  HsSelectModule,
  HsSelectOption,
} from '@hsetu/ngui-components';
import { HsLayerDirective } from '@hsetu/ngui-resources';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ArticleItem } from '@modules/shopping/models/article-item.model';
import { PriceTotal } from '@modules/shopping/models/price-total.model';
import { GuidGeneratorService } from '@shared/tools/guid-generator.service';
import { KaufmannSettingsService } from '@modules/settings/services/kfm-settings.service';
import {
  ARTICLE_NUMBER_LABEL,
  CANCEL_LABEL,
  MANUFACTURER_LABEL,
  OK_LABEL,
  QUANTITY_LABEL,
  SHORT_TEXT_LABEL,
  UNIT_LABEL,
} from '@modules/shopping/i18n/label-text-constants';

@Component({
  selector: 'kfm-article-dialog',
  standalone: true,
  imports: [
    CommonModule,
    HsSearchBarModule,
    HsLayerDirective,
    HsButtonModule,
    HsInputModule,
    HsFormFieldModule,
    HsSelectModule,
  ],
  templateUrl: './kfm-article-dialog.component.html',
})
export class KfmArticleDialogComponent {
  private readonly _data = inject(DIALOG_DATA);
  private readonly _guidGenerator = inject(GuidGeneratorService);
  private readonly _settings = inject(KaufmannSettingsService);
  private readonly defaultVat = this._settings.getDefaultVatRate();
  private lastEditedPriceField: 'net' | 'gross' = 'net';

  protected readonly cancelLabel = CANCEL_LABEL;
  protected readonly okLabel = OK_LABEL;
  protected readonly unitLabel = UNIT_LABEL;
  protected readonly quantityLabel = QUANTITY_LABEL;
  protected readonly articleNumberLabel = ARTICLE_NUMBER_LABEL;
  protected readonly manufacturerLabel = MANUFACTURER_LABEL;
  protected readonly shortTextLabel = SHORT_TEXT_LABEL;
  protected showAmount = false;
  protected readonly vatLabel = 'Mehrwertsteuer';
  protected readonly netLabel = 'Netto';
  protected readonly grossLabel = 'Brutto';

  protected readonly articleMwstOptions: HsSelectOption<string>[] =
    this._settings.getVatOptions().map((v) => ({ value: v, label: v }));

  protected readonly dialogRef = inject<DialogRef<ArticleItem>>(
    DialogRef<ArticleItem>,
  );

  protected readonly selectedArticle = signal<ArticleItem | undefined>(
    undefined,
  );

  protected get canSave(): boolean {
    return !!(
      this.selectedArticle()?.infoDesc &&
      this.selectedArticle()?.infoDesc?.trim().length
    );
  }

  protected readonly title = computed(() => {
    return this.selectedArticle()?.infoDesc ?? '';
  });

  protected readonly manufacturer = computed(() => {
    return this.selectedArticle()?.manufacturerLabel ?? '';
  });

  protected readonly articleNumber = computed(() => {
    return this.selectedArticle()?.articleNumber ?? '';
  });

  protected readonly amount = computed(() => {
    return this.selectedArticle()?.amount ?? 0;
  });

  protected readonly unit = computed(() => {
    return this.selectedArticle() ? this.selectedArticle().unit : 'ST';
  });

  protected readonly mwstAsOption = computed(() => {
    return `${this.selectedArticle()?.priceTotal?.vat ?? this.defaultVat}%`;
  });

  protected readonly netTotal = computed(() => {
    return this.selectedArticle()?.priceTotal?.netTotal ?? 0;
  });

  protected readonly grossTotal = computed(() => {
    return this.selectedArticle()?.priceTotal?.grossTotal ?? 0;
  });

  constructor() {
    if (this._data?.['showAmount']) {
      this.showAmount = this._data['showAmount'];
    }

    if (this._data && this._data['item']) {
      this.selectedArticle.set({ ...this._data['item'] });
      this.ensurePriceValues();
    } else {
      this.selectedArticle.set({
        uId: this._guidGenerator.emptyUUID,
        tenantUId: this._guidGenerator.emptyUUID,
        userUId: this._guidGenerator.emptyUUID,
        concurrencyIdentificator: this._guidGenerator.emptyUUID,
        syncType: 0,
        concurrencyStamp: new Date().toISOString(),
        stampCreate: new Date().toISOString(),
        amount: 1,
        unit: 'ST',
        taskId: '',
        infoDesc: '',
        priceTotal: {
          vat: this.defaultVat,
          netTotal: 0,
          vatTotal: 0,
          grossTotal: 0,
        },
      });
    }
  }

  protected onTitleChanged(newTitle: string): void {
    const article = this.selectedArticle();
    if (!article) {
      return;
    }

    article.infoDesc = newTitle.trim();
    this.selectedArticle.set({ ...article });
  }

  protected onManufacturerChanged(newManufacturer: string): void {
    const article = this.selectedArticle();
    if (!article) {
      return;
    }

    article.manufacturerLabel = newManufacturer.trim();
    this.selectedArticle.set({ ...article });
  }

  protected onArticleNumberChanged(newArticleNumber: string): void {
    const article = this.selectedArticle();
    if (!article) {
      return;
    }

    article.articleNumber = newArticleNumber.trim();
    this.selectedArticle.set({ ...article });
  }

  protected onAmountChanged(newAmount: string): void {
    const article = this.selectedArticle();
    if (!article) {
      return;
    }

    article.amount = this.parseNumber(newAmount);
    this.selectedArticle.set({ ...article });
  }

  protected onUnitChanged(newUnit: string): void {
    const article = this.selectedArticle();
    if (!article) {
      return;
    }

    article.unit = newUnit.trim();
    this.selectedArticle.set({ ...article });
  }

  protected onSelectionChanged(
    value: string | { value?: string },
    name: string,
  ): void {
    if (name !== 'mwst') {
      return;
    }

    if (typeof value === 'string') {
      this.onMwstChanged(value);
      return;
    }

    this.onMwstChanged(value?.value ?? '0%');
  }

  protected onMwstChanged(newMwst: string): void {
    const article = this.selectedArticle();
    if (!article) {
      return;
    }

    this.ensurePriceTotal(article).vat = this.parsePercent(newMwst);

    if (this.lastEditedPriceField === 'gross') {
      this.recalculateFromGross();
      return;
    }

    this.recalculateFromNet();
  }

  protected onNetTotalChanged(newNetTotal: string): void {
    const article = this.selectedArticle();
    if (!article) {
      return;
    }

    this.lastEditedPriceField = 'net';
    this.ensurePriceTotal(article).netTotal = this.parseNumber(newNetTotal);
    this.recalculateFromNet();
  }

  protected onGrossTotalChanged(newGrossTotal: string): void {
    const article = this.selectedArticle();
    if (!article) {
      return;
    }

    this.lastEditedPriceField = 'gross';
    this.ensurePriceTotal(article).grossTotal = this.parseNumber(newGrossTotal);
    this.recalculateFromGross();
  }

  private recalculateFromNet(): void {
    const article = this.selectedArticle();
    if (!article) {
      return;
    }

    const priceTotal = this.ensurePriceTotal(article);
    const netTotal = priceTotal.netTotal ?? 0;
    const vat = priceTotal.vat ?? 0;
    const vatTotal = this.roundTo2(netTotal * (vat / 100));
    const grossTotal = this.roundTo2(netTotal + vatTotal);

    priceTotal.vat = vat;
    priceTotal.vatTotal = vatTotal;
    priceTotal.grossTotal = grossTotal;

    this.selectedArticle.set({ ...article });
  }

  private recalculateFromGross(): void {
    const article = this.selectedArticle();
    if (!article) {
      return;
    }

    const priceTotal = this.ensurePriceTotal(article);
    const grossTotal = priceTotal.grossTotal ?? 0;
    const vat = priceTotal.vat ?? 0;
    const divisor = 1 + vat / 100;
    const netTotal = divisor === 0 ? 0 : this.roundTo2(grossTotal / divisor);
    const vatTotal = this.roundTo2(grossTotal - netTotal);

    priceTotal.vat = vat;
    priceTotal.netTotal = netTotal;
    priceTotal.vatTotal = vatTotal;
    priceTotal.grossTotal = this.roundTo2(grossTotal);

    this.selectedArticle.set({ ...article });
  }

  private ensurePriceValues(): void {
    const article = this.selectedArticle();
    if (!article) {
      return;
    }

    const priceTotal = this.ensurePriceTotal(article);

    priceTotal.vat = priceTotal.vat ?? this.defaultVat;
    priceTotal.netTotal = priceTotal.netTotal ?? 0;
    priceTotal.vatTotal = priceTotal.vatTotal ?? 0;
    priceTotal.grossTotal = priceTotal.grossTotal ?? 0;

    if ((priceTotal.grossTotal ?? 0) > 0 && !(priceTotal.netTotal ?? 0)) {
      this.recalculateFromGross();
      return;
    }

    this.recalculateFromNet();
  }

  private ensurePriceTotal(article: ArticleItem): PriceTotal {
    article.priceTotal ??= {};
    return article.priceTotal;
  }

  private parsePercent(value: string): number {
    const parsedValue = parseFloat(value.replace('%', '').replace(',', '.'));
    return isNaN(parsedValue) ? 0 : parsedValue;
  }

  private parseNumber(value: string): number {
    const parsedValue = parseFloat(value.replace(',', '.'));
    return isNaN(parsedValue) ? 0 : parsedValue;
  }

  private roundTo2(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }
}
